'use client';

import { useTranslations } from 'next-intl';
import { DisplayPrice } from '@/shared/components/display-price';
import { useAppStore } from '@/features/shared/app-store';
import { GameImageDisplay } from '@/features/games/shared/components/game-image-display';
import { useEffect, useState } from 'react';
import { Button } from '@/shared/components/primitives/button';
import { useOverlayStore } from '@/shared/components/overlay/overlay.store';
import { useModalContext } from '@/shared/components/overlay/use-modal-context';
import { GameData, PurchaseOption } from '@/features/games/shared/types/game-data';
import { PriceIncludingWildMembership } from '@/shared/types/prices';
import Loader from '@/shared/components/loader';
import { REDIRECT_CHECKOUT_ROUTE } from '@/shared/constants/routes';

export const ItemAddedOverlay = ({
  gameData,
  purchasePrice,
  purchaseType = 'buy',
  closeDisabled = false,
}: {
  gameData: GameData;
  purchasePrice?: PriceIncludingWildMembership | null;
  purchaseType: PurchaseOption['type'];
  closeDisabled?: boolean;
}) => {
  const t = useTranslations('Cart');
  const { hideOverlay } = useOverlayStore();

  const items = useAppStore((s) => s.cart.items);

  const isInCart = items.some((item) => item.productEntityId === gameData.entityId);

  const { setHeader } = useModalContext();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isCloseDisabled = closeDisabled || isRedirecting;

  useEffect(() => {
    setHeader(
      <h1 className='font-sans-bold flex-1 px-4 text-center text-xl'>{t('itemAddedToCart')}</h1>,
    );
  }, [setHeader, t]);

  const handleCheckout = () => {
    setIsRedirecting(true);
    window.location.href = REDIRECT_CHECKOUT_ROUTE;
  };

  return (
    <div className='flex gap-4 p-6'>
      <div className='w-[300px] shrink-0'>
        {gameData.defaultImage && (
          <GameImageDisplay
            onClick={isCloseDisabled ? undefined : hideOverlay}
            image={gameData.defaultImage}
            href={gameData?.path}
            imageSize='square'
            sizes='300px'
          />
        )}
      </div>

      <div className='flex flex-1 flex-col'>
        <div>
          {gameData.developer?.value && (
            <p className='text-sm text-gray-500'>{gameData.developer.value}</p>
          )}

          <h2 className='font-sans-bold text-3xl'>{gameData.name}</h2>

          <div className='mt-2'>
            {Boolean(purchasePrice?.value) && (
              <DisplayPrice
                className='text-primary font-semibold'
                value={purchasePrice!.value}
                currencyCode={purchasePrice!.currencyCode}
              />
            )}
          </div>

          <p className='mt-2'>
            <strong>{t('purchase')}:</strong>
            <span className='text-gray-200'> {t(purchaseType)}</span>
          </p>
        </div>

        <div className='mt-auto flex gap-4 pt-6'>
          <Button
            onClick={handleCheckout}
            disabled={!isInCart || isRedirecting}
            loading={isRedirecting}
            variant='primary'
            className='flex-1'
          >
            {!isInCart ? <Loader label='' /> : ''}
            {t('proceedToCheckout')}
          </Button>

          <Button
            onClick={hideOverlay}
            disabled={!isInCart || isRedirecting}
            loading={isRedirecting}
            variant='secondary'
            className='flex-1'
          >
            {!isInCart ? <Loader label='' /> : ''}
            <span>{t('continueShopping')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
